import Jsontableify from "jsontableify";

import React, { useRef, useState } from "react";
import { useLocation } from 'react-router-dom';
import {
  MDBCard,
  MDBCardBody,
  MDBCardHeader,
  MDBCardTitle,
  MDBTypography,
} from "mdb-react-ui-kit";
import {
  capitalizeFirstLetter,
  convertISOToReadableDate,
} from "../../../helpers";
import { ReportActionButtons } from "../../screened-manuscripts/components/ReportActionButtons";
import "./DuplicateSubmissionsDataReview.css";
import { dropdownOptions } from "../dropdownOptions";
import { ApplyAction } from "../../../components/ApplyAction";
import DuplicateSubmissionsDiffViewer from "./DuplicateSubmissionsDiffViewer";
import ConfirmationDialog from "../../../components/ConfirmationDialog/ConfirmationDialog";

const OTHER_PUBLISHER_NAME = 'Other publisher';
const DEEPLINK_PATH = '/dlreport';
const ACTIONS_REQUIRING_CONFIRMATION = [
  "duplicate confirmed",
  "suspect paper mill"
];

const DuplicateSubmissionsDataReview = ({
  data,
  withSubHeader,
  onActionSelect,
}) => {
  const abstractRef = useRef(null);

  const location = useLocation();
  const isDeepLinkRoute = location.pathname.startsWith(DEEPLINK_PATH);

  const [confirmationDialog, setConfirmationDialog] = useState({
    isOpen: false,
    actionType: null,
    actionContext: null
  });

  const [llmAnalysisText, setLlmAnalysisText] = useState(null);

  const formatDate = (date, format = "DD MMM YYYY HH:mm") =>
    date ? convertISOToReadableDate(date, format) : "";

  const getStatusSummary = (item, isOtherPublisher) => {
    const results = [];
    const { submission_id, submission_status, status_updated, archived_timestamp } = item;

    const formatMessage = (baseMessage, timestamp) => {
      return timestamp ? `${baseMessage} (${formatDate(timestamp)})` : baseMessage;
    };

    if (["duplicate confirmed", "suspect paper mill"].includes(submission_status)) {
      results.push(formatMessage("Duplicate Confirmed", status_updated));
    } else if (["false positive", "false match"].includes(submission_status)) {
      results.push(formatMessage("Reported False Positive", status_updated));
    }

    if (!submission_id) {
      results.push(formatMessage("Submission Removed", archived_timestamp));
    }

    return capitalizeFirstLetter(results.join("; "));
  };

  const formatReport = (data) => {
    const isDifferentDate = (date1, date2) => {
      if (!date1 || !date2) return false;
      const d1 = new Date(date1);
      const d2 = new Date(date2);
      return (
        d1.getFullYear() !== d2.getFullYear() ||
        d1.getMonth() !== d2.getMonth() ||
        d1.getDate() !== d2.getDate()
      );
    };

    const formatSubmissionDate = (submittedDate, originalDate) => {
      const formattedSubmitted = formatDate(submittedDate);
      const formattedOriginal =
        originalDate && isDifferentDate(submittedDate, originalDate)
          ? `(original: ${formatDate(originalDate, "DD MMM YYYY")}) `
          : "";

      return `
          <span>
            ${formattedSubmitted} ${formattedOriginal
          ? `<span class="highlighted-status">${formattedOriginal}</span>`
          : ""}
            ${formattedOriginal ? '<span class="tooltip-placeholder"></span>' : ""}
          </span>
        `;
    };

    const formatPublisher = (email, manuscriptId, name) => {
      return email
        ? `<a href="mailto:${email}?subject=${manuscriptId}">${name}</a>`
        : name;
    };

    function formatMetrics(fulltextData, llmComparisonData) {
      const metrics = [];
      if (fulltextData?.similarity != null) {
        metrics.push(`Similarity: ${Math.round(fulltextData.similarity * 100)}%`);
      }
      if (fulltextData?.jaccard_similarity != null) {
        metrics.push(`Jaccard: ${Math.round(fulltextData.jaccard_similarity * 100)}%`);
      }
      if (fulltextData?.euclidean_similarity != null) {
        metrics.push(`Euclidean: ${Math.round(fulltextData.euclidean_similarity * 100)}%`);
      }
      if (fulltextData?.correlation_distance != null) {
        metrics.push(`Correlation Dist: ${fulltextData.correlation_distance.toFixed(2)}`);
      }
      if (fulltextData?.distance != null) {
        metrics.push(`Cosine Dist: ${fulltextData.distance.toFixed(2)}`);
      }

      if (llmComparisonData.similarityScore != null) {
        metrics.push(`LLM Similarity: ${Math.round(llmComparisonData.similarityScore * 100)}%`);
      }
      return metrics;
    }

    const formatManuscript = (
      {
        submitted,
        manuscript_id: manuscriptId,
        journal,
        publisher_name: publisherName,
        title,
        feedback_email: feedbackEmail,
        original_submission_date: originalSubmissionDate,
        abstract,
        authors,
        similarity,
        persistent_id,
        ...rest
      },
      llmComparisonData = null
    ) => {
      const {
        title: titleSimilarity,
        authors: authorsSimilarity,
        abstract: abstractSimilarity,
      } = similarity ?? {};

      const fulltextData = rest?.fulltext;
      const fulltextSimilarity = fulltextData?.similarity
        ? Math.round(fulltextData.similarity * 100)
        : null;

      const isOtherPublisher = publisherName.includes(OTHER_PUBLISHER_NAME);
      const formattedTitle =
        title + (titleSimilarity ? ` (Similarity: ${titleSimilarity}%)` : "");
      const submissionStatus = getStatusSummary(rest, isOtherPublisher);

      const abstractText = abstract ? abstract.slice(0, 200) : "";
      const isAbstractTruncated = abstract && abstract.length >= 200;
      const showSimilarityOnNewLine = abstract && abstract.length <= 100;

      let abstractInfo = {};
      if (abstract || abstractSimilarity) {
        const similarityText = abstractSimilarity
          ? `${abstract ? " (" : ""}Similarity: ${abstractSimilarity}%${abstract ? ")" : ""}`
          : "";

        abstractInfo = {
          Abstract: `${abstractText}${isAbstractTruncated
            ? `... <a href="#" class="view-more-link">View more</a>`
            : ""
            }${showSimilarityOnNewLine ? "<br>" : " "}${similarityText}`,
        };
      }

      let authorsInfo = {};
      if (authors || authorsSimilarity) {
        const similarityText = authorsSimilarity
          ? `${authors ? " (" : ""}Similarity: ${authorsSimilarity}%${authors ? ")" : ""}`
          : "";

        authorsInfo = {
          "Author list": `${authors ? authors.join(", ") : ""}${authorsSimilarity ? `${similarityText}` : ""}`,
        };
      }

      let fulltextInfo = {};
      if (fulltextSimilarity) {
        fulltextInfo = {
          "Full-text": `Similarity: ${fulltextSimilarity}%`,
        };
      }

      let ftsAnalysisInfo = {};
      if (fulltextData) {
        const metrics = formatMetrics(fulltextData, llmComparisonData || {});

        const metricsHtml = metrics.length > 0
          ? `<div class="fts-metrics">${metrics.join('<br>')}</div>`
          : '';

        let llmAnalysisHtml;
        if (llmComparisonData) {
          // Escape the opinion text for safe HTML attribute storage
          const escapedOpinion = llmComparisonData.opinion
            .replace(/&/g, '&amp;')
            .replace(/'/g, '&#39;')
            .replace(/"/g, '&quot;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
          llmAnalysisHtml = `<a href="#" class="view-llm-analysis-link text-primary" data-opinion="${escapedOpinion}">View LLM Analysis</a>`;
        } else {
          llmAnalysisHtml = `<span class="text-danger">No LLM analysis available</span>`;
        }

        ftsAnalysisInfo = {
          "FTS Analysis": `
            <div class="fts-analysis-summary">
              ${metricsHtml}
              ${llmAnalysisHtml}
            </div>
          `,
        };
      }

      return {
        ...(submissionStatus && {
          Status: `<span class="highlighted-status">${submissionStatus}</span>`,
        }),
        Date: formatSubmissionDate(submitted, originalSubmissionDate),
        Publisher: isOtherPublisher
          ? `<span class="blured-line">${publisherName}</span>`
          : formatPublisher(feedbackEmail, manuscriptId, publisherName),
        Journal: isOtherPublisher
          ? `<span class="blured-line">${journal}</span>`
          : journal,
        ManuscriptID: isOtherPublisher
          ? `<span class="blured-line">${manuscriptId}</span>`
          : manuscriptId,
        Title: formattedTitle,
        ...abstractInfo,
        ...authorsInfo,
        ...fulltextInfo,
        ...ftsAnalysisInfo,
      };
    };

    const { matches, trigger } = data;

    // Create a lookup map for LLM comparisons by persistentId
    const llmComparisonMap = {};
    if (trigger?.llm_comparison) {
      trigger.llm_comparison.forEach((comparison) => {
        llmComparisonMap[comparison.persistentId] = comparison;
      });
    }

    const reportData = formatManuscript(trigger);
    const formattedMatches =
      matches?.map((match) => {
        // Find the LLM comparison for this match using persistent_id
        const llmData = llmComparisonMap[match.persistent_id] || null;
        return formatManuscript(match, llmData);
      }) || null;

    return {
      ...reportData,
      "Matched Manuscripts": formattedMatches,
    };
  };

  const handleInterceptedAction = (index, value) => {
    if (ACTIONS_REQUIRING_CONFIRMATION.includes(value)) {
      setConfirmationDialog({
        isOpen: true,
        actionType: value,
        actionContext: { index, value }
      });
      return;
    }

    onActionSelect(index, value);
  };

  const handleConfirmationApply = (options) => {
    const { actionContext } = confirmationDialog;

    if (actionContext) {
      const fullParams = {
        ...(options.addToSignalcheck && {
          signalCheck: {
            user: { comment: options.comment || "" },
            manuscript: {
              title: data?.trigger?.title,
              manuscript_id: data?.manuscript_id,
              publisher_id: data?.trigger?.publisher_id,
              user_id: data?.trigger?.user_id,
            },
          },
        }),
      };
      onActionSelect(actionContext.index, actionContext.value, fullParams);
    }

    setConfirmationDialog({
      isOpen: false,
      actionType: null,
      actionContext: null
    });
  };

  const handleConfirmationClose = () => {
    setConfirmationDialog({
      isOpen: false,
      actionType: null,
      actionContext: null
    });
  };

  const handleActionUndo = async (index) => {
    await onActionSelect(index, null);
  };

  const handleViewMore = (e) => {
    e.preventDefault();
    const target = e.target;
    if (target.classList.contains("view-more-link")) {
      abstractRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    if (target.classList.contains("view-llm-analysis-link")) {
      const opinion = target.getAttribute("data-opinion");
      if (opinion) {
        // Unescape the HTML entities
        const unescapedOpinion = opinion
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .replace(/&amp;/g, '&');
        setLlmAnalysisText(unescapedOpinion);
      }
    }
  };

  const renderModalContent = (data) => {
    if (!data) return null;

    const reportData = formatReport(data);
    const jt = new Jsontableify();
    const content = jt.toHtml(reportData).html;

    const SHOULD_SHOW_TITLE_DIFF =
      data?.trigger?.authors &&
      data?.matches?.[0]?.authors &&
      data?.trigger?.abstract &&
      data?.matches?.[0]?.abstract;

    return (
      <>
        <div
          className="modal_table_wrapper"
          dangerouslySetInnerHTML={{ __html: content }}
          onClick={handleViewMore}
        />

        <hr className="hr" />
        {llmAnalysisText && (
          <div className="my-4">
            <MDBTypography tag="h6" className="d-flex align-items-center justify-content-between">
              LLM Analysis
              <a
                href="#"
                className="text-muted small"
                onClick={(e) => { e.preventDefault(); setLlmAnalysisText(null); }}
              >
                Close
              </a>
            </MDBTypography>
            <div className="llm-opinion p-3 bg-light rounded" style={{ whiteSpace: 'pre-wrap' }}>
              {llmAnalysisText}
            </div>
          </div>
        )}
        {SHOULD_SHOW_TITLE_DIFF && (
          <DuplicateSubmissionsDiffViewer
            label="Title"
            oldValue={data?.trigger?.title}
            newValue={data?.matches?.[0]?.title}
            similarity={data?.matches?.[0]?.similarity?.title}
            oldValueTitle={data.trigger?.publisher_name}
            newValueTitle={data.matches[0]?.publisher_name}
          />
        )}
        <DuplicateSubmissionsDiffViewer
          label="Authors list"
          oldValue={data?.trigger?.authors}
          newValue={data?.matches?.[0]?.authors}
          similarity={data?.matches?.[0]?.similarity?.authors}
          oldValueTitle={data.trigger?.publisher_name}
          newValueTitle={data.matches[0]?.publisher_name}
        />
        <DuplicateSubmissionsDiffViewer
          ref={abstractRef}
          label="Abstract"
          oldValue={data?.trigger?.abstract}
          newValue={data?.matches?.[0]?.abstract}
          similarity={data?.matches?.[0]?.similarity?.abstract}
          oldValueTitle={data.trigger?.publisher_name}
          newValueTitle={data.matches[0]?.publisher_name}
        />
        {data?.matches?.[0]?.similarity?.fulltext && (
          <div className="my-4">
            <MDBTypography tag="h6">
              Full-text (Similarity: {data.matches[0].similarity.fulltext}%)
            </MDBTypography>
            <div className="text-muted">
              Full-text content cannot be displayed due to access restrictions.
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <ConfirmationDialog
        actionType={confirmationDialog.actionType}
        isOpen={confirmationDialog.isOpen}
        onClose={handleConfirmationClose}
        onApply={handleConfirmationApply}
      />

      {(!!data?.matches || !!data?.trigger) && (
        <MDBCard className="my-2">
          {withSubHeader && (
            <MDBCardHeader className="d-flex align-items-center">
              <ReportActionButtons
                signals={["duplicateSubmissions"]}
                iconOnly
              />
              <MDBCardTitle className="pt-2 ps-2">
                Duplicate submissions
              </MDBCardTitle>
              <div className="ms-auto">
                <ApplyAction
                  index={data?.manuscript_id}
                  status={data?.submission_status}
                  selectAction={handleInterceptedAction}
                  undoAction={handleActionUndo}
                  options={dropdownOptions}
                />
              </div>
            </MDBCardHeader>
          )}
          <MDBCardBody>{renderModalContent(data)}</MDBCardBody>
        </MDBCard>
      )}
    </>
  );
};

export default DuplicateSubmissionsDataReview;
